// Import required libraries
import React, { useContext, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import axios from "axios";
import { v4 } from "uuid";
import orderBy from "lodash/orderBy";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

// Import custom components
import NewReportManager from "./NewReportManager";
import SigviewButton from "../../components/Common/SigviewButton";
import SigviewNav from "../../components/Common/SigviewNav";
import SigviewTooltip from "../../components/Common/SigviewTooltip";
import { ThemeContext } from "../../contexts/ThemeContext";
import { makeSigviewStyles } from "../../utils/pivotUtils";
import SigviewTypography from "../../components/Common/SigviewTypography";
import SigviewSearchField from "../../components/Common/SigviewSearchField/SigviewSearchField";
import ReportTable from "./ReportTable";
import ReportCard from "./ReportCard";
// Import styles
import "./NewReportManager.scss";

// Import config
import { config } from "../../config/config";

// Import utils
import {
  timeSinceCreation,
  capitalize,
  getSigviewUserType,
  isShareReportValidBasedOnUserAccess,
} from "../../utils/utils";
import {
  makeSubtitleForScheduledReport,
  getSelectedReportPayload,
  unwrapperBucketListInReport,
  unwrapperSavedReport,
  unwrapperScheduledReport,
} from "../../utils/reportUtils";
import { makeAttributeMissingErrorMessage } from "../../utils/analyzeUtils";
import { masterTrackGaEvent } from "../../services/ga";

// Import data
import triggerFrequencyArr from "../../../assets/data/triggerFrequencyArr.json";
// Import Images
import activeGrid from "../../../assets/images/activeGrid.svg";
import inActiveGrid from "../../../assets/images/inActiveGrid.svg";
import activeTable from "../../../assets/images/activeTable.svg";
import inActiveTable from "../../../assets/images/inActiveTable.svg";

// Import api functions
import {
  getAllScheduledReports,
  getAllSavedReports,
  getReportsDownloadHistory,
  fetchBucketList,
  getBookmark,
} from "../../services/api";
// Import Layout
import LayoutTopSideBottom from "../../layouts/LayoutTopSideBottom/LayoutTopSideBottom";
import i18n from "../../i18n";

const reportTypesArr = [
  { id: "saved", name: "Saved Reports", disabled: false },
  { id: "scheduled", name: "Scheduled Reports", disabled: false },
  { id: "download", name: "Download History" },
];

const initialData = { status: "loading", message: "", data: [] };

function ReportHome(props) {
  const { t } = useTranslation();
  const {
    dispatch: ReduxDispatcher,
    user,
    allData,
    globalFilters = {},
  } = props;

  const sigviewUserType = getSigviewUserType();

  const history = useHistory();
  const sidenavProps = { tabName: "reports" };
  // * Define required hooks/contexts
  const { state: themeState } = useContext(ThemeContext);
  const themeColors = themeState.themes[themeState.activeTheme];
  const useSigviewStyles = makeSigviewStyles(themeColors);
  const classes = useSigviewStyles();
  // Dummy data for each tab
  const [view, setView] = useState("grid");
  const [isSharedReport, setIsSharedReport] = useState(false);
  const [sharedReportData, setSharedReportData] = useState({});
  const [reportSheet, setReportSheet] = useState(initialData);
  const [showReportManager, setShowReportManager] = useState(false); //mount and unmount report manager everytime user changes active report
  const [scheduledReports, setScheduledReports] = useState(initialData);
  // To get the maxSavedReport
  const maxSavedReports = user.uiLimitsList.maxSavedReports;
  // To get the maxScheduledReports
  const maxScheduledReports = user.uiLimitsList.maxScheduledReports;
  const [savedReports, setSavedReports] = useState(initialData);
  const [reportsDownloadHistory, setReportsDownloadHistory] =
    useState(initialData);
  const [reloadSavedReports, setReloadSavedReports] = useState(false);
  const [reloadScheduledReports, setReloadScheduledReports] = useState(false);
  const [reloadReportsDownloadHistory, setReloadReportsDownloadHistory] =
    useState(false);
  const [activeReportType, setActiveReportType] = useState("saved");
  const [activeReport, setActiveReport] = useState({
    type: "none",
    id: "",
    variant: "normal",
    selections: {},
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [tourRun, setTourRun] = useState(false);
  const [bucketList, setBucketList] = useState({
    result: [],
    status: "initial",
    message: "",
  });

  const handleViewChange = (newView) => {
    if (newView !== null) setView(newView);
  };

  //Defining required variables
  const reportsDownloadHistoryTransformed = {
    status: reportsDownloadHistory.status,
    message: reportsDownloadHistory.message,
    //1) We are first removing the invalid reports
    //2) We are then transforming them for sidenav
    //3) We are ordering by created data desc
    data: orderBy(
      reportsDownloadHistory.data
        .filter(
          (row) =>
            getSelectedReportPayload({
              activeReport: {
                type: "download",
                id: row.id,
              },
              allReports: {
                scheduledReports: scheduledReports.data,
                savedReports: savedReports.data,
                reportsDownloadHistory: reportsDownloadHistory.data,
              },
              allData,
              user,
              globalFilters,
            }).valid
        )
        .map((row) => ({
          id: row.id,
          title: row.name,
          subtitle: `${t("Downloaded")} ${timeSinceCreation(row.createdOn)}`,
          payload: row,
          isCurrentlySelected: activeReport.id === row.id,
          createdOn: parseInt(row.createdOn),
        })),
      (row) => row.createdOn,
      ["desc"]
    ),
  };
  const savedReportsTransformed = {
    status: savedReports.status,
    message: savedReports.message,
    data: orderBy(
      savedReports.data
        .filter(
          (row) =>
            getSelectedReportPayload({
              activeReport: {
                type: "saved",
                id: row.recentDownloadRecord.id,
              },
              allReports: {
                scheduledReports: scheduledReports.data,
                savedReports: savedReports.data,
                reportsDownloadHistory: reportsDownloadHistory.data,
              },
              allData,
              user,
              globalFilters,
            }).valid
        )
        .map((row) => ({
          id: row.recentDownloadRecord.id,
          title: row.recentDownloadRecord.name,
          subtitle: `${t("Created")} ${timeSinceCreation(
            row.recentDownloadRecord.createdOn
          )}`,
          payload: row,
          isCurrentlySelected: activeReport.id === row.recentDownloadRecord.id,
          createdOn: parseInt(row.recentDownloadRecord.createdOn),
        })),
      (row) => row.createdOn,
      ["desc"]
    ),
  };
  const scheduledReportsTransformed = {
    status: scheduledReports.status,
    message: scheduledReports.message,
    data: orderBy(
      scheduledReports.data
        .filter(
          (row) =>
            getSelectedReportPayload({
              activeReport: {
                type: "scheduled",
                id: row._id,
              },
              allReports: {
                scheduledReports: scheduledReports.data,
                savedReports: savedReports.data,
                reportsDownloadHistory: reportsDownloadHistory.data,
              },
              allData,
              user,
              globalFilters,
            }).valid
        )
        .map((row) => ({
          id: row._id,
          title: row.reportName,
          subtitle: makeSubtitleForScheduledReport(row, triggerFrequencyArr),
          isActive: row.reportStatus === "active",
          payload: row,
          isCurrentlySelected: activeReport.id === row._id,
          createdOn: parseInt(row.createdOn),
        })),
      (row) => row.createdOn,
      ["desc"]
    ),
  };

  //Defining required useEffects
  //Scheduled Reports
  useEffect(() => {
    //Make fetch call using axios
    const source = axios.CancelToken.source();
    const fetchProps = {
      cancelToken: source.token,
    };
    const getAllScheduledReportsPromise = getAllScheduledReports(fetchProps);
    getAllScheduledReportsPromise
      .then((responseData) => {
        let newData = {
          status: "success",
          message:
            responseData?.result?.data?.length || [].length !== 0
              ? ""
              : "No scheduled reports to display",
          data: responseData?.result?.data || [],
        };
        setScheduledReports(newData);
      })
      .catch((json) => {
        if (json.error !== config.hardCoded.queryCancelled) {
          let newData = { status: "error", message: json.error, data: [] };
          setScheduledReports(newData);
          console.groupCollapsed("UI ERROR");
          console.log("ERROR ->", json.error);
          console.groupEnd();
        }
      });

    if (sigviewUserType === "nonSigview") {
      const fetchBucketListPromise = fetchBucketList({});
      fetchBucketListPromise
        .then((data) => {
          let newData = {
            result: data.result.data,
            status: "success",
            messsage: "",
          };
          setBucketList(newData);
        })
        .catch((json) => {
          let newData = {
            result: [],
            status: "error",
            messsage: "Fetch bucketList API failed",
          };
          setBucketList(newData);
        });
    }

    return () => {
      setScheduledReports(initialData);
      //Cancel all previous fetch calls
      if (source) source.cancel();
    };
  }, [reloadScheduledReports]);

  //Saved Reports
  useEffect(() => {
    //Make fetch call using axios
    const source = axios.CancelToken.source();
    const fetchProps = {
      cancelToken: source.token,
    };
    const getAllSavedReportsPromise = getAllSavedReports(fetchProps);
    getAllSavedReportsPromise
      .then((responseData) => {
        let newData = {
          status: "success",
          message:
            responseData?.result?.data?.length || [].length !== 0
              ? ""
              : "No saved reports to display",
          data: responseData?.result?.data || [],
        };
        setSavedReports(newData);
      })
      .catch((json) => {
        if (json.error !== config.hardCoded.queryCancelled) {
          let newData = { status: "error", message: json.error, data: [] };
          setSavedReports(newData);
          console.groupCollapsed("UI ERROR");
          console.log("ERROR ->", json.error);
          console.groupEnd();
        }
      });

    return () => {
      setSavedReports(initialData);
      //Cancel all previous fetch calls
      if (source) source.cancel();
    };
  }, [reloadSavedReports]);

  //Reports Download History
  useEffect(() => {
    //Make fetch call using axios
    const source = axios.CancelToken.source();
    const fetchProps = {
      cancelToken: source.token,
    };
    const getReportsDownloadHistoryPromise =
      getReportsDownloadHistory(fetchProps);
    getReportsDownloadHistoryPromise
      .then((responseData) => {
        var dataFinal;
        if (sigviewUserType === "sigview") {
          dataFinal = responseData?.result?.data || [];
        } else {
          dataFinal = responseData?.result?.data || [];
        }
        let newData = {
          status: "success",
          message: dataFinal.length !== 0 ? "" : "No downloads",
          data: dataFinal || [],
        };
        setReportsDownloadHistory(newData);
      })
      .catch((json) => {
        if (json.error !== config.hardCoded.queryCancelled) {
          let newData = { status: "error", message: json.error, data: [] };
          setReportsDownloadHistory(newData);
          console.groupCollapsed("UI ERROR");
          console.log("ERROR ->", json.error);
          console.groupEnd();
        }
      });

    return () => {
      setReportsDownloadHistory(initialData);
      //Cancel all previous fetch calls
      if (source) source.cancel();
    };
  }, [reloadReportsDownloadHistory]);

  // If the report is goto reports
  useEffect(() => {
    if (user.screen.reportManager.metadata.variant === "gotoReport") {
      const selections = user.screen.reportManager.metadata.selections;
      setActiveReport({
        type: "saved",
        id: `report-${v4()}`,
        variant: "gotoReport",
        selections,
      });
    }
  }, []);
  //Change showReportManager state so that the component can mount and unmount for resetting formData
  useEffect(() => {
    setShowReportManager(false);
  }, [activeReport]);

  //Change showReportManager state so that the component can mount and unmount for resetting formData
  useEffect(() => {
    setShowReportManager(true);
  }, [showReportManager]);

  //DEBUGGING
  // console.group("REPORT MANAGER CONTAINER STATE");
  // console.log("scheduledReports", scheduledReports);
  // console.log("activeReport", activeReport);
  // console.log("savedReports", savedReports);
  // console.log("reportsDownloadHistory", reportsDownloadHistory);
  // console.log("activeReportFormData", activeReportFormData);
  // console.log(
  //   "reportsDownloadHistoryTransformed",
  //   reportsDownloadHistoryTransformed
  // );
  // console.log("scheduledReportsTransformed", scheduledReportsTransformed);
  // console.log("savedReportsTransformed", savedReportsTransformed);
  // console.groupEnd();

  let disabledAddNewReport = false;
  let disabledAddNewReportButtonMessage;
  if (activeReportType === "saved") {
    if (savedReports.status === "loading") {
      disabledAddNewReport = true;
      disabledAddNewReportButtonMessage = "loading";
    } else {
      if (savedReports.data.length >= maxSavedReports) {
        disabledAddNewReport = true;
        disabledAddNewReportButtonMessage =
          "Max Limit Reached For Saved Reports";
      } else {
        disabledAddNewReport = false;
        disabledAddNewReportButtonMessage = "";
      }
    }
  } else {
    if (scheduledReports.status === "loading") {
      disabledAddNewReport = true;
      disabledAddNewReportButtonMessage = "loading";
    } else {
      if (scheduledReports.data.length >= maxScheduledReports) {
        disabledAddNewReport = true;
        disabledAddNewReportButtonMessage =
          "Max Limit Reached For Scheduled Reports";
      } else {
        disabledAddNewReport = false;
        disabledAddNewReportButtonMessage = "";
      }
    }
  }

  let ModifiedBucketList = useMemo(
    () =>
      bucketList.result.length > 0
        ? [{ id: "initial", name: "Select Storage", disabled: true }].concat(
            unwrapperBucketListInReport(bucketList.result)
          )
        : [
            {
              id: "NA",
              name: "No storage available for your realm",
              disabled: true,
            },
          ],
    [bucketList.result]
  );

  const reloadData = (reportType) => {
    if (reportType === "saved") {
      setReloadSavedReports((prevState) => !prevState);
      setActiveReportType("saved");
    } else if (reportType === "scheduled") {
      setReloadScheduledReports((prevState) => !prevState);
      setActiveReportType("scheduled");
    } else if (reportType === "download") {
      setReloadReportsDownloadHistory((prevState) => !prevState);
    }
  };

  const fetchData = async () => {
    const sharedReportId = localStorage.getItem("sharedReportId");
    if (!sharedReportId) return;

    const source = axios.CancelToken.source();
    const payload = {
      bookmarkId: sharedReportId,
      isShare: "true",
    };
    const fetchProps = { payload, cancelToken: source.token };

    setShowReportManager(false);

    try {
      const response = await getBookmark(fetchProps);
      const { data } = response.result;

      const reportPayload = data.profile.report.savedReportPayload;
      const scheduledPayload = data.profile.report.scheduledReportPayload;
      const isShareReportValidBasedOnUserAccessProps = {
        backendWs: reportPayload || scheduledPayload,
        user,
        allData,
      };
      const isShareReportValidBasedOnUserAccessResponse =
        isShareReportValidBasedOnUserAccess(
          isShareReportValidBasedOnUserAccessProps
        );

      if (isShareReportValidBasedOnUserAccessResponse.status !== "valid") {
        const message = makeAttributeMissingErrorMessage(
          isShareReportValidBasedOnUserAccessResponse,
          "Report"
        );
        setReportSheet({ status: "accessError", message });
        localStorage.removeItem("sharedReportId");
        return;
      }

      const reportType = reportPayload
        ? "saved"
        : scheduledPayload
        ? "scheduled"
        : "";
      if (sharedReportId) {
        setActiveReport({ type: reportType, id: sharedReportId });
      }

      setReportSheet({ status: "success", message: "" });

      const reportFormData =
        reportType === "saved"
          ? unwrapperSavedReport({
              payload: {
                orgViewReq: data.profile.orgViewReq,
                email: data.profile.sharedBy,
                recentDownloadRecord: reportPayload,
              },
              allData,
              user,
              activeReport: { type: "saved", id: sharedReportId },
            })
          : unwrapperScheduledReport({
              payload: scheduledPayload,
              allData,
              user,
              activeReport: { type: "scheduled", id: sharedReportId },
            });
      reportFormData.reportName = "Untitled Report";
      setSharedReportData({
        reportFormData,
        reportPayload:
          reportType === "saved" ? reportPayload : scheduledPayload,
      });
      setIsSharedReport(true);
      setShowReportManager(true);
    } catch (error) {
      console.error("Error fetching bookmark:", error);
      // Handle error state here if needed
    } finally {
      source.cancel();
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAccessCorruptError = () => {
    history.push("/analyze/dashboard");
    window.location.reload();
  };
  const handleReportRowClick = ({ type, id }) => {
    setIsSharedReport(false);
    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "ReportManager",
      action: "OpenReport",
      label: capitalize(type),
    });
    //Update active report only when a different report is clicked otherwise the form will reset
    if (id !== activeReport.id) {
      setActiveReport({ type, id });
    }
  };

  const handleReportNavClick = (type) => {
    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "ReportManager",
      action: "SwitchTab",
      label: capitalize(type),
    });
    setActiveReportType(type);
  };
  const reportsSidenavData =
    activeReportType === "saved"
      ? { ...savedReportsTransformed }
      : activeReportType === "scheduled"
      ? { ...scheduledReportsTransformed }
      : { ...reportsDownloadHistoryTransformed };
  let activeReportFormData = getSelectedReportPayload({
    activeReport,
    allReports: {
      scheduledReports: scheduledReports.data,
      savedReports: savedReports.data,
      reportsDownloadHistory: reportsDownloadHistory.data,
    },
    allData,
    user,
    globalFilters,
  });
  activeReportFormData = {
    ...activeReportFormData,
    reportFormData: {
      ...activeReportFormData.reportFormData,
      preferredLanguage: i18n.language === "ja" ? "ja" : "en",
    },
  };
  const updateParentScheduledReportsOnToggle = (id, payload) => {
    setScheduledReports((prevState) => ({
      ...prevState,
      data: scheduledReports.data.map((row) =>
        row._id !== id ? row : payload
      ),
    }));
  };
  const handleAddNewReport = (gaLabel) => {
    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "ReportManager",
      action: "AddNewReport",
      label: gaLabel,
    });
    setIsSharedReport(false);
    setActiveReport({ type: activeReportType, id: `report-${v4()}` });
  };
  const handleReportSearchFieldChange = (event, value) => setSearchQuery(value);

  return (
    <LayoutTopSideBottom sidenavProps={sidenavProps}>
      {activeReport.type === "none" ? (
        <Box p={3}>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <SigviewTypography variant="heading" style={{ fontSize: "20px" }}>
                Reports
              </SigviewTypography>
              <Box
                className={classes.searchContainer}
                style={{
                  minWidth: "250px",
                  marginLeft: "16px",
                }}
              >
                <SigviewSearchField
                  onChange={handleReportSearchFieldChange}
                  placeholder="Search"
                  customStyle={{
                    wrapperBgColor: "#f8f9fd",
                    wrapperBorderColor: "1px solid #fff",
                    wrapperBorderHover: "none",
                    wrapperBorderFocus: "#fff",
                  }}
                />
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              {/* Grid/Table Toggle */}
              <Box display="flex" gap="6px">
                <SigviewTooltip title="Grid View" placement="bottom">
                  <Box
                    onClick={() => handleViewChange("grid")}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "5px",
                      boxShadow:
                        view === "grid"
                          ? "0px 4px 12px 0px rgba(84, 72, 206, 0.2)"
                          : "none",
                      p: "6px", // reduced padding
                      transition: "box-shadow 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={view === "grid" ? activeGrid : inActiveGrid}
                      alt="Grid View"
                      style={{ width: 17, height: 17 }}
                    />
                  </Box>
                </SigviewTooltip>
                <SigviewTooltip title="Table View" placement="bottom">
                  <Box
                    onClick={() => handleViewChange("table")}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "5px",
                      boxShadow:
                        view === "table"
                          ? "0px 4px 12px 0px rgba(84, 72, 206, 0.2)"
                          : "none",
                      p: "6px",
                      transition: "box-shadow 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={view === "table" ? activeTable : inActiveTable}
                      alt="Table View"
                      style={{ width: 17, height: 17 }}
                    />
                  </Box>
                </SigviewTooltip>
              </Box>

              {/* Add New Report */}
              <SigviewTooltip
                title={disabledAddNewReportButtonMessage}
                placement="bottom"
              >
                <Box>
                  <SigviewButton
                    customClassName="AddNewReport-GA"
                    variant="containedPrimary"
                    disabled={disabledAddNewReport}
                    title="Add New Report"
                    iconProps={{
                      className: "material-icons-round",
                      iconName: "add",
                    }}
                    iconPosition="end"
                    iconFlag={true}
                    style={{
                      fontSize: "14px",
                      buttonHeight: "34px",
                      borderRadius: "999em",
                    }}
                    onClick={() => handleAddNewReport("Form")}
                  />
                </Box>
              </SigviewTooltip>
            </Box>
          </Box>

          {/* Tabs */}
          <SigviewNav
            data={reportTypesArr}
            value={activeReportType}
            style={{
              fontSize: "11px",
              textTransform: "uppercase",
              padding: "0px 11px",
            }}
            onChange={(e, newValue) => handleReportNavClick(newValue)}
            customClassName="ReportSwitchTab"
          />

          {/* Grid or Table */}
          {view === "grid" ? (
            <ReportCard
              data={reportsSidenavData}
              reportType={activeReportType}
              user={user}
              onToggle={updateParentScheduledReportsOnToggle}
              onClick={handleReportRowClick}
              searchQuery={searchQuery}
              activeReport={activeReport}
              setActiveReport={setActiveReport}
              reloadData={reloadData}
            />
          ) : (
            <ReportTable
              data={reportsSidenavData}
              user={user}
              onToggle={updateParentScheduledReportsOnToggle}
              reportType={activeReportType}
              rowKey="id"
              onRowClick={handleReportRowClick}
              searchQuery={searchQuery}
              activeReport={activeReport}
              setActiveReport={setActiveReport}
              reloadData={reloadData}
            />
          )}
        </Box>
      ) : (
        <div className="new-report-manager-container">
          <section className="content-container">
            {showReportManager && (
              <>
                {activeReportFormData.valid ? (
                  <NewReportManager
                    activeReportFormData={
                      isSharedReport ? sharedReportData : activeReportFormData
                    }
                    activeReport={activeReport}
                    setActiveReport={setActiveReport}
                    ReduxDispatcher={ReduxDispatcher}
                    user={user}
                    allData={allData}
                    globalFilters={globalFilters}
                    reloadData={reloadData}
                    tourRun={tourRun}
                    setTourRun={setTourRun}
                    uiFeatureList={user.uiFeatureList}
                    disabledAddNewReport={disabledAddNewReport}
                    bucketList={ModifiedBucketList}
                    setShowReportManager={setShowReportManager}
                    isSharedReport={isSharedReport}
                    setIsSharedReport={setIsSharedReport}
                  />
                ) : (
                  <div className="right-panel no-report-selected-container">
                    {t("CORRUPT REPORT")}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </LayoutTopSideBottom>
  );
}
const mapStateToProps = (state) => ({
  user: state.user,
  allData: state.data,
  globalFilters: state.globalFilters,
});

export default connect(mapStateToProps)(ReportHome);
